import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAdminScrape, useAdminAutoScrape, useAdminImportEvents, type ImportEventItem } from '../../hooks/useAdmin'
import ChurchSearchCombobox from '../../components/ui/ChurchSearchCombobox'
import { ApiError } from '../../lib/api'
import type { AdminChurch } from '../../types/admin'
import type { AdminScrapeResult, AdminAutoScrapeResult } from '../../types/admin'

const CLAUDE_PROMPT = `You are extracting church event data from the provided content (images, text, or fliers).

Return ONLY a valid JSON array. Each element must follow this exact shape:

[
  {
    "title": "Event name",
    "eventType": "potluck",
    "description": "Optional short description (1-2 sentences)",
    "startsAt": "2026-06-07T13:00:00",
    "endsAt": "2026-06-07T15:00:00",
    "addressText": "123 Main St, City, State ZIP"
  }
]

Event type values (use exactly one):
  potluck   — shared meals, fellowship dinners, potluck lunches
  worship   — church services, prayer meetings, vespers, evangelistic series
  study     — Bible study, Sabbath school, small groups, life groups
  social    — game nights, youth socials, non-meal gatherings, parties
  outdoors  — hikes, nature walks, camping, sports
  kids      — VBS, Pathfinders, children's ministry, youth group
  service   — community outreach, volunteer events, mission projects

Rules:
- Dates must be ISO 8601: YYYY-MM-DDTHH:MM:SS (no timezone suffix)
- If the year is not stated, use the next upcoming occurrence
- If no end time is given, omit "endsAt"
- If the event is at the church itself, omit "addressText" — the church address will be used
- Output ONLY the raw JSON array. No markdown, no explanation, no code fences.`

function CopyPromptBox() {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    void navigator.clipboard.writeText(CLAUDE_PROMPT).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="rounded-[10px] border border-line-soft bg-s2 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-line-soft">
        <span className="text-[12px] font-semibold text-ink-2">Claude prompt — paste this before your flier images</span>
        <button
          type="button"
          onClick={handleCopy}
          className="text-[12px] px-3 py-1 rounded-md border border-line-soft bg-surface text-ink font-semibold cursor-pointer font-sans"
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <pre className="m-0 px-4 py-3 text-[11px] leading-[1.6] text-ink-2 font-mono overflow-x-auto whitespace-pre-wrap">
        {CLAUDE_PROMPT}
      </pre>
    </div>
  )
}

function formatDateShort(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export default function AdminScrapePage() {
  // ── URL scrape ──────────────────────────────────────────────────────────────
  const [sourceUrl, setSourceUrl] = useState('')
  const [urlAutoFilled, setUrlAutoFilled] = useState(false)
  const [selectedScrapeChurch, setSelectedScrapeChurch] = useState<AdminChurch | null>(null)
  const [scrapeResult, setScrapeResult] = useState<AdminScrapeResult | null>(null)
  const [scrapeError, setScrapeError] = useState<{ code: string; message: string } | null>(null)

  const scrape = useAdminScrape()
  const autoScrape = useAdminAutoScrape()
  const [autoScrapeResult, setAutoScrapeResult] = useState<AdminAutoScrapeResult | null>(null)
  const [autoScrapeError, setAutoScrapeError] = useState<{ code: string; message: string } | null>(null)

  function handleAutoScrape() {
    if (!selectedScrapeChurch) return
    setAutoScrapeResult(null)
    setAutoScrapeError(null)
    setScrapeResult(null)
    setScrapeError(null)
    autoScrape.mutate({ churchId: selectedScrapeChurch.id }, {
      onSuccess: (res) => setAutoScrapeResult(res.data),
      onError: (err) => {
        if (err instanceof ApiError) setAutoScrapeError({ code: err.code, message: err.message })
        else setAutoScrapeError({ code: 'UNKNOWN', message: 'Auto-scrape failed' })
      },
    })
  }

  function handleScrapeChurchSelect(church: AdminChurch) {
    setSelectedScrapeChurch(church)
    if (church.sourceUrl && !sourceUrl.trim()) {
      setSourceUrl(church.sourceUrl)
      setUrlAutoFilled(true)
    }
  }

  function handleScrapeChurchClear() {
    setSelectedScrapeChurch(null)
    if (urlAutoFilled) {
      setSourceUrl('')
      setUrlAutoFilled(false)
    }
  }

  function handleUrlChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSourceUrl(e.target.value)
    setUrlAutoFilled(false)
  }

  function handleScrapeSubmit(e: React.FormEvent) {
    e.preventDefault()
    setScrapeResult(null)
    setScrapeError(null)
    const body: { sourceUrl: string; churchId?: string } = { sourceUrl: sourceUrl.trim() }
    if (selectedScrapeChurch) body.churchId = selectedScrapeChurch.id
    scrape.mutate(body, {
      onSuccess: (res) => setScrapeResult(res.data),
      onError: (err) => {
        if (err instanceof ApiError) setScrapeError({ code: err.code, message: err.message })
        else setScrapeError({ code: 'UNKNOWN', message: 'Scrape failed' })
      },
    })
  }

  // ── Manual JSON import ──────────────────────────────────────────────────────
  const [selectedImportChurch, setSelectedImportChurch] = useState<AdminChurch | null>(null)
  const [jsonText, setJsonText] = useState('')
  const [parsed, setParsed] = useState<ImportEventItem[] | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ imported: number; total: number; errors: string[] } | null>(null)

  const importEvents = useAdminImportEvents()

  function handleImportChurchSelect(church: AdminChurch) {
    setSelectedImportChurch(church)
    setParsed(null)
    setImportResult(null)
  }

  function handleImportChurchClear() {
    setSelectedImportChurch(null)
    setParsed(null)
    setImportResult(null)
  }

  function handleParse() {
    setParseError(null)
    setParsed(null)
    try {
      const raw: unknown = JSON.parse(jsonText.trim())
      if (!Array.isArray(raw)) throw new Error('Expected a JSON array at the top level')
      const items = raw as ImportEventItem[]
      for (const [i, item] of items.entries()) {
        if (typeof item.title !== 'string' || !item.title) throw new Error(`Event ${i + 1}: missing "title"`)
        if (typeof item.startsAt !== 'string' || !item.startsAt) throw new Error(`Event ${i + 1}: missing "startsAt"`)
        if (isNaN(new Date(item.startsAt).getTime())) throw new Error(`Event ${i + 1}: invalid "startsAt" date`)
      }
      setParsed(items)
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Invalid JSON')
    }
  }

  function handleImport() {
    if (!parsed || !selectedImportChurch) return
    setImportResult(null)
    importEvents.mutate(
      { churchId: selectedImportChurch.id, events: parsed },
      { onSuccess: (res) => { setImportResult(res.data); setParsed(null); setJsonText('') } },
    )
  }

  const fieldClass = 'px-3 py-2.5 rounded-lg border border-line-soft bg-bg text-[14px] font-sans w-full'

  return (
    <div className="flex flex-col gap-10 max-w-[640px]">

      {/* ── URL scrape ───────────────────────────────────────────────────────── */}
      <div>
        <div className="mb-5">
          <h1 className="m-0 text-[24px] font-bold">Scrape events</h1>
          <p className="text-[13px] text-muted mt-2 leading-[1.5] m-0">
            Fetch a church website, extract events with Claude, and create unverified scraped listings.
            Respects <code className="text-[12px]">robots.txt</code>. One URL at a time — this can take 30–60 seconds.
          </p>
        </div>

        <form onSubmit={handleScrapeSubmit} className="bg-surface rounded-[14px] border border-line-soft p-6 flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-semibold text-ink-2">Church events page URL</span>
            <input
              type="url"
              required
              value={sourceUrl}
              onChange={handleUrlChange}
              placeholder="https://examplechurch.org/events"
              className={fieldClass}
            />
            {urlAutoFilled && (
              <span className="text-[11px] text-muted">Pre-filled from church record — edit if needed</span>
            )}
          </label>
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-semibold text-ink-2">
              Link to existing church <span className="font-normal text-muted">(optional — auto-fills URL if church has one saved)</span>
            </span>
            <ChurchSearchCombobox
              selected={selectedScrapeChurch}
              onSelect={handleScrapeChurchSelect}
              onClear={handleScrapeChurchClear}
              placeholder="Search by church name…"
              optional
            />
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={scrape.isPending || autoScrape.isPending || !sourceUrl.trim()}
              className="px-5 py-2.5 rounded-lg bg-[#1a1a2e] text-white text-[14px] font-semibold border-0 cursor-pointer font-sans disabled:opacity-50">
              {scrape.isPending ? 'Scraping…' : 'Run scrape'}
            </button>
            {selectedScrapeChurch && (
              <button
                type="button"
                disabled={scrape.isPending || autoScrape.isPending}
                onClick={handleAutoScrape}
                className="px-5 py-2.5 rounded-lg border border-line-soft bg-s2 text-[14px] font-semibold cursor-pointer font-sans disabled:opacity-50"
              >
                {autoScrape.isPending ? 'Finding events page…' : 'Auto-scrape'}
              </button>
            )}
          </div>
          {selectedScrapeChurch && !autoScrape.isPending && (
            <p className="text-[11px] text-muted -mt-2 m-0">
              Auto-scrape finds the events page automatically — no URL needed.
            </p>
          )}
        </form>

        {scrapeError && (
          <div className="mt-4 px-4 py-3 rounded-xl border border-danger-line bg-danger-soft text-danger-deep text-[13px]" role="alert">
            <strong>{scrapeError.code}</strong> — {scrapeError.message}
          </div>
        )}
        {scrapeResult && (
          <div className="mt-4 px-4 py-4 rounded-xl border border-line-soft bg-s2 text-[13px]">
            <div className="text-[11px] font-semibold text-muted uppercase tracking-[0.08em] mb-2">Scrape complete</div>
            <ul className="m-0 pl-5 flex flex-col gap-1">
              <li>Church ID: <code className="text-[12px]">{scrapeResult.churchId}</code></li>
              <li>Events created: {scrapeResult.eventsCreated}</li>
              <li>Events skipped (duplicates): {scrapeResult.eventsSkipped}</li>
            </ul>
            <p className="mt-3 mb-0 text-muted text-[12px]">
              View on the <Link to="/admin/churches" className="text-primary">Churches</Link> tab or search the map after refresh.
            </p>
          </div>
        )}

        {autoScrapeError && (
          <div className="mt-4 px-4 py-3 rounded-xl border border-danger-line bg-danger-soft text-danger-deep text-[13px]" role="alert">
            <strong>{autoScrapeError.code}</strong> — {autoScrapeError.message}
          </div>
        )}
        {autoScrapeResult && (
          <div className="mt-4 px-4 py-4 rounded-xl border border-line-soft bg-s2 text-[13px]">
            <div className="text-[11px] font-semibold text-muted uppercase tracking-[0.08em] mb-2">Auto-scrape complete</div>
            <ul className="m-0 pl-5 flex flex-col gap-1">
              <li>Events page: <code className="text-[12px] break-all">{autoScrapeResult.foundUrl}</code></li>
              <li>Events created: {autoScrapeResult.eventsCreated}</li>
              <li>Events skipped (duplicates): {autoScrapeResult.eventsSkipped}</li>
            </ul>
            <p className="mt-3 mb-0 text-muted text-[12px]">
              View on the <Link to="/admin/churches" className="text-primary">Churches</Link> tab or search the map after refresh.
            </p>
          </div>
        )}
      </div>

      {/* ── Manual JSON import ───────────────────────────────────────────────── */}
      <div>
        <div className="mb-5">
          <h2 className="m-0 text-[20px] font-bold">Manual import</h2>
          <p className="text-[13px] text-muted mt-2 leading-[1.5] m-0">
            Scan fliers or images with Claude using the prompt below, then paste the resulting JSON here to import events for a specific church.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <CopyPromptBox />

          <div className="bg-surface rounded-[14px] border border-line-soft p-6 flex flex-col gap-4">

            {/* Church search */}
            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] font-semibold text-ink-2">Church</span>
              <ChurchSearchCombobox
                selected={selectedImportChurch}
                onSelect={handleImportChurchSelect}
                onClear={handleImportChurchClear}
                placeholder="Search by church name…"
              />
            </div>

            {/* JSON textarea */}
            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] font-semibold text-ink-2">Paste JSON from Claude</span>
              <textarea
                value={jsonText}
                onChange={(e) => { setJsonText(e.target.value); setParsed(null); setParseError(null) }}
                rows={10}
                placeholder={'[\n  {\n    "title": "Sabbath Potluck",\n    "eventType": "potluck",\n    "startsAt": "2026-06-07T12:30:00"\n  }\n]'}
                className="px-3 py-2.5 rounded-lg border border-line-soft bg-bg text-[13px] font-mono resize-y w-full font-sans"
                style={{ minHeight: 160 }}
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleParse}
                disabled={!jsonText.trim() || !selectedImportChurch}
                className="px-4 py-2 rounded-lg border border-line-soft bg-s2 text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-40"
              >
                Parse &amp; preview
              </button>
              {parsed && (
                <button type="button" onClick={handleImport} disabled={importEvents.isPending}
                  className="px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50">
                  {importEvents.isPending ? 'Importing…' : `Import ${parsed.length} event${parsed.length === 1 ? '' : 's'}`}
                </button>
              )}
            </div>

            {parseError && (
              <div className="px-3 py-2 rounded-lg border border-danger-line bg-danger-soft text-danger-deep text-[13px]">
                {parseError}
              </div>
            )}

            {/* Preview table */}
            {parsed && parsed.length > 0 && (
              <div className="rounded-[10px] border border-line-soft overflow-hidden">
                <table className="w-full border-collapse text-[12px]">
                  <thead>
                    <tr className="bg-s2 border-b border-line-soft">
                      <th className="px-3 py-2 text-left font-semibold text-ink-2">Title</th>
                      <th className="px-3 py-2 text-left font-semibold text-ink-2">Type</th>
                      <th className="px-3 py-2 text-left font-semibold text-ink-2">Starts</th>
                      <th className="px-3 py-2 text-left font-semibold text-ink-2">Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((ev, i) => (
                      <tr key={i} className={i < parsed.length - 1 ? 'border-b border-line-soft' : ''}>
                        <td className="px-3 py-2 font-medium text-ink">{ev.title}</td>
                        <td className="px-3 py-2 text-muted">{ev.eventType}</td>
                        <td className="px-3 py-2 text-muted whitespace-nowrap">{formatDateShort(ev.startsAt)}</td>
                        <td className="px-3 py-2 text-muted">{ev.addressText ?? <span className="italic">church address</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Import result */}
            {importResult && (
              <div className="px-4 py-3 rounded-xl border border-line-soft bg-s2 text-[13px]">
                <div className="font-semibold text-good-deep mb-1">
                  ✓ Imported {importResult.imported} of {importResult.total} events
                </div>
                {importResult.errors.length > 0 && (
                  <ul className="m-0 pl-4 text-danger-deep text-[12px] mt-1">
                    {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}
